import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultMST3020_R01HeaderDto, MST3020_R01HeaderDto } from './Dto_MST3020_R01';
import { useMST3020_R01Store } from './Store_MST3020_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'MST3020_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST3020_R01HeaderDto> | null>;
}

export const Table_MST3020_R01 = (props: Props) => {
	const { gridRef } = props;

	const {
		MST3020_R01List,
		selectedMST3020_R01,
		setSelectedMST3020_R01,
		initSelectedMST3020_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useMST3020_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'itemCd',
		gridData: MST3020_R01List,
		defaultGridData: defaultMST3020_R01HeaderDto,
		selectedRow: selectedMST3020_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		modelNo: '모델명',
		brand: '브랜드',
		itemGroup: '품목그룹',
		itemAccount: '품목계정',
		stockQty: '재고량',
		remark: '비고',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'itemCd')(params), []);

	const baseColumnDefs: ColDef<MST3020_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 95,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 210,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
	], [getCellStyle]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST3020_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST3020_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<MST3020_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedMST3020_R01?.itemCd === rowData.itemCd;
		setSelectedMST3020_R01(isSelected ? undefined : rowData);
		setDetailScrollTop(null);
	}, [selectedMST3020_R01, setSelectedMST3020_R01, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		itemCd: `${MST3020_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [MST3020_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [MST3020_R01List, selectedMST3020_R01]);

	return (
		<GridContextWrapper<MST3020_R01HeaderDto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedMST3020_R01}
			defaultRowData={defaultMST3020_R01HeaderDto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST3020_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<MST3020_R01HeaderDto>({
					idKey: 'itemCd',
					selectedList: selectedMST3020_R01 ? [selectedMST3020_R01.itemCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
